import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../AppIcon';
import { fetchTags } from '../../api/tags';

/**
 * Sélection multiple de tags : recherche, choix existants ou création inline (un seul champ).
 */
export default function TagMultiSelect({
  value = [],
  onChange,
  label = 'Tags',
  placeholder = 'Rechercher ou créer un tag…',
  disabled = false,
  className = '',
}) {
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => (Array.isArray(value) ? value.map((t) => String(t).trim()).filter(Boolean) : []),
    [value],
  );

  const loadTags = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchTags();
      const tags = res?.data?.tags || [];
      setOptions(tags.map((t) => String(t.name || '').trim()).filter(Boolean));
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTags();
  }, [loadTags]);

  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const searchTrim = search.trim();
  const searchLower = searchTrim.toLowerCase();

  const dropdownOptions = useMemo(() => {
    const pool = [...new Set([...options, ...selected])].filter(
      (n) => !selected.includes(n),
    );
    if (!searchTrim) return pool.slice(0, 40);
    return pool.filter((n) => n.toLowerCase().includes(searchLower)).slice(0, 40);
  }, [options, selected, searchTrim, searchLower]);

  const canCreate =
    searchTrim.length > 0 &&
    !selected.some((s) => s.toLowerCase() === searchLower) &&
    !options.some((o) => o.toLowerCase() === searchLower);

  const emit = (next) => {
    onChange?.(next);
  };

  const addTag = (name) => {
    const n = String(name || '').trim();
    if (!n || selected.includes(n)) return;
    emit([...selected, n]);
    if (!options.includes(n)) {
      setOptions((prev) => [...prev, n].sort((a, b) => a.localeCompare(b, 'fr')));
    }
    setSearch('');
    inputRef.current?.focus();
  };

  const removeTag = (name) => {
    emit(selected.filter((t) => t !== name));
  };

  const onInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (canCreate) addTag(searchTrim);
      else if (dropdownOptions.length === 1) addTag(dropdownOptions[0]);
    }
    if (e.key === 'Backspace' && !search && selected.length > 0) {
      removeTag(selected[selected.length - 1]);
    }
    if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div className={className} ref={rootRef}>
      {label ? (
        <label className="text-sm font-medium text-foreground mb-2 block">{label}</label>
      ) : null}
      <div
        className={`relative rounded-md border border-input bg-background text-sm focus-within:ring-2 focus-within:ring-ring ${
          disabled ? 'opacity-60 pointer-events-none' : ''
        }`}
        onClick={() => {
          if (!disabled) {
            setOpen(true);
            inputRef.current?.focus();
          }
        }}
      >
        <div className="flex flex-wrap items-center gap-1.5 px-2 py-2 min-h-[2.5rem]">
          {selected.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs max-w-full"
            >
              <span className="truncate">{tag}</span>
              <button
                type="button"
                className="p-0.5 rounded-full hover:bg-primary/20 shrink-0"
                onClick={(ev) => {
                  ev.stopPropagation();
                  removeTag(tag);
                }}
                aria-label={`Retirer ${tag}`}
              >
                <Icon name="X" size={10} />
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            type="text"
            value={search}
            disabled={disabled}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onInputKeyDown}
            placeholder={selected.length === 0 ? placeholder : ''}
            className="flex-1 min-w-[8rem] bg-transparent border-0 outline-none px-1 py-0.5 text-foreground placeholder:text-muted-foreground"
          />
        </div>

        {open && !disabled ? (
          <div className="absolute z-50 left-0 right-0 top-full mt-1 max-h-52 overflow-auto rounded-md border border-border bg-card shadow-md py-1">
            {loading ? (
              <p className="px-3 py-2 text-xs text-muted-foreground">Chargement…</p>
            ) : null}
            {canCreate ? (
              <button
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-smooth flex items-center gap-2"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addTag(searchTrim)}
              >
                <Icon name="Plus" size={14} className="text-primary shrink-0" />
                <span>
                  Créer « <span className="font-medium">{searchTrim}</span> »
                </span>
              </button>
            ) : null}
            {dropdownOptions.length === 0 && !canCreate ? (
              <p className="px-3 py-2 text-xs text-muted-foreground">
                {searchTrim ? 'Aucun tag correspondant.' : 'Tapez pour filtrer ou créer.'}
              </p>
            ) : (
              dropdownOptions.map((name) => (
                <button
                  key={name}
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-smooth"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => addTag(name)}
                >
                  {name}
                </button>
              ))
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
